// Altas del padrón de la cadena (vendedores, tiendas y zonas) en `retail-org`: permisos y errores.
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, userMessageOf } from '@/api/errors';
import { usePermissions } from '@/kit';
import { INVENTORY_QUERY_KEY } from '../inventario/api/useInventarioLens';

/** Claves que exige cada endpoint de alta de `retail-org-business` (`@RequirePermission`). */
export const ROSTER_PERMISSIONS = {
    createUser: 'retail.user.create',
    createStore: 'retail.store.create',
    createZone: 'retail.zone.create',
    changeUserStatus: 'retail.user.status',
    deleteUser: 'retail.user.delete',
    updateUser: 'retail.user.update',
} as const;

export interface RosterAbilities {
    canCreateUser: boolean;
    canCreateStore: boolean;
    canCreateZone: boolean;
    canChangeUserStatus: boolean;
    canDeleteUser: boolean;
    canUpdateUser: boolean;
}

export function useRosterAbilities(): RosterAbilities {
    const { hasPermission } = usePermissions();
    return {
        canCreateUser: hasPermission(ROSTER_PERMISSIONS.createUser),
        canCreateStore: hasPermission(ROSTER_PERMISSIONS.createStore),
        canCreateZone: hasPermission(ROSTER_PERMISSIONS.createZone),
        canChangeUserStatus: hasPermission(ROSTER_PERMISSIONS.changeUserStatus),
        canDeleteUser: hasPermission(ROSTER_PERMISSIONS.deleteUser),
        canUpdateUser: hasPermission(ROSTER_PERMISSIONS.updateUser),
    };
}

/** Campo del formulario que se marca con el error, si lo hay. */
export type RosterErrorField = 'email' | 'storeId' | 'code' | 'location' | 'zoneId' | 'phone';

export interface RosterWriteError {
    code: string;
    message: string;
    field?: RosterErrorField;
}

const NO_PERMISSION = 'No tienes permiso para hacer este cambio en tu cadena.';
const RBAC_DOWN =
    'No pudimos registrar el acceso en este momento. Intenta de nuevo en unos minutos; si sigue pasando, repórtalo.';

const EMAIL_TAKEN = {
    message: 'Ese correo ya está registrado fuera de tu cadena. Usa otro correo.',
    field: 'email',
} as const;

/** Un correo ya registrado en la cadena no falla: el alta le agrega el rol. */
const RULE_BY_CODE: Partial<Record<string, Omit<RosterWriteError, 'code'>>> = {
    USER_ALREADY_EXISTS: EMAIL_TAKEN,
    RETAIL_USER_ALREADY_EXISTS: {
        message: 'Ese correo ya tiene un usuario en el padrón.',
        field: 'email',
    },
    RETAIL_USER_IN_OTHER_RETAILER: {
        message: 'Esa persona ya pertenece a otra cadena; no se puede dar de alta aquí.',
        field: 'email',
    },
    RETAIL_USER_INACTIVE: {
        message: 'Esta persona ya está en tu cadena pero desactivada. Actívala desde su detalle.',
        field: 'email',
    },
    RETAIL_USER_NOT_FOUND: {
        message: 'Este vendedor ya no existe en la cadena. Recarga la lista.',
    },
    SELLER_HAS_OTHER_ACCESS: {
        message:
            'Esta persona tiene otros accesos fuera de tu cadena; pídele a un Admin SK que la gestione.',
    },
    USER_DOES_NOT_HAVE_RETAILER_ROLE: {
        message:
            'El usuario no tiene un rol en tu cadena. Pide que se lo asignen y vuelve a intentar.',
    },
    STORE_NOT_IN_RETAILER: {
        message: 'La tienda elegida no es de tu cadena. Elige otra.',
        field: 'storeId',
    },
    DUPLICATE_STORE_CODE: {
        message: 'Ya existe una tienda con esa clave en tu cadena. Usa otra.',
        field: 'code',
    },
    INVALID_STORE_LOCATION: {
        message:
            'La ubicación no es válida: la latitud va de -90 a 90 y la longitud de -180 a 180.',
        field: 'location',
    },
    ZONE_NOT_FOUND: {
        message: 'La zona elegida ya no existe. Cierra el formulario, recarga y elige otra.',
        field: 'zoneId',
    },
    IDEMPOTENCY_IN_PROGRESS: {
        message: 'Tu alta anterior sigue en proceso. Espera un momento y revisa la lista.',
    },
    VALIDATION_ERROR: { message: 'Revisa los datos del formulario: hay campos inválidos.' },
    MISSING_RETAILER_SCOPE: {
        message: 'Tu usuario no tiene una cadena asignada. Contacta al administrador.',
    },
    FORBIDDEN: { message: NO_PERMISSION },
    PERMISSION_DENIED: { message: NO_PERMISSION },
    PLATFORM_RBAC_UNAVAILABLE: { message: RBAC_DOWN },
    RBAC_LEVEL_ENTITY_MISSING: { message: RBAC_DOWN },
    RBAC_PARENT_LEVEL_MISSING: { message: RBAC_DOWN },
};

/** Traduce el `code` del backend al mensaje y al campo que ve el usuario. */
export function toRosterWriteError(error: unknown): RosterWriteError {
    if (error instanceof ApiError) {
        const rule = RULE_BY_CODE[error.code];
        if (rule) return { code: error.code, ...rule };
        return { code: error.code, message: userMessageOf(error) };
    }
    return { code: 'UNKNOWN', message: userMessageOf(error) };
}

/** Refresca las pantallas del Portal Admin que leen el padrón, para que el alta aparezca sin recargar. */
export function useInvalidateRoster(): () => Promise<void> {
    const qc = useQueryClient();
    return async () => {
        await Promise.all([
            qc.invalidateQueries({ queryKey: ['retail-admin', 'roster'] }),
            qc.invalidateQueries({ queryKey: ['retail-admin', 'vendedores'] }),
            qc.invalidateQueries({ queryKey: ['retail-admin', 'tiendas'] }),
            qc.invalidateQueries({ queryKey: [...INVENTORY_QUERY_KEY, 'stores'] }),
            qc.invalidateQueries({ queryKey: [...INVENTORY_QUERY_KEY, 'zones'] }),
        ]);
    };
}
